import { useState } from "react"
import { Link, router } from "@inertiajs/react"
import { Pencil, Trash2 } from "lucide-react"
import { BackofficeLayout } from "@/components/layout/BackofficeLayout"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import type { NomineeShareholderTypeSummary, DocumentAttachment } from "@/types"
import { DocumentUploader } from "@/components/shared/DocumentUploader"
import { formatCents } from '@/lib/utils'

type Props = {
  type: NomineeShareholderTypeSummary & { document_templates: DocumentAttachment[] }
}

export default function NomineeShareholderTypesShow({ type }: Props) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  return (
    <BackofficeLayout
      title={type.name}
      breadcrumbs={[
        { label: "Backoffice", href: "/backoffice/dashboard" },
        { label: "Nominee Shareholder Types", href: "/backoffice/nominee_shareholder_types" },
        { label: type.name },
      ]}
    >
      <div className="mb-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">{type.name}</h1>
            <p className="text-sm text-muted-foreground font-mono">{type.code}</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" asChild className="h-8 text-[13px]">
              <Link href={`/backoffice/nominee_shareholder_types/${type.id}/edit`}>
                <Pencil className="mr-1.5 h-3 w-3" />Edit
              </Link>
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowDeleteDialog(true)} className="h-8 text-[13px] text-destructive hover:text-destructive">
              <Trash2 className="mr-1.5 h-3 w-3" />Delete
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Details</h2>
          </div>
          <div className="px-4 py-4 space-y-4">
            {type.description && (
              <div>
                <div className="text-sm font-medium text-muted-foreground">Description</div>
                <div className="text-sm">{type.description}</div>
              </div>
            )}
            <div>
              <div className="text-sm font-medium text-muted-foreground">Jurisdiction</div>
              <div className="text-sm">{type.jurisdiction_label || type.jurisdiction_code}</div>
            </div>
            <Separator />
            <div>
              <div className="text-sm font-medium text-muted-foreground">Service Fee</div>
              <div className="text-sm">{formatCents(type.service_fee_cents)}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">Government Fee</div>
              <div className="text-sm">{formatCents(type.gov_fee_cents)}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground">Minimum Duration</div>
              <div className="text-sm">{type.min_duration_months} months</div>
            </div>
            {type.processing_days_min && type.processing_days_max && (
              <div>
                <div className="text-sm font-medium text-muted-foreground">Processing Time</div>
                <div className="text-sm">{type.processing_days_min}-{type.processing_days_max} days</div>
              </div>
            )}
          </div>
        </section>

        <DocumentUploader
          title="Document Templates"
          documents={type.document_templates || []}
          uploadPath={`/backoffice/nominee_shareholder_types/${type.id}/document_templates`}
          deletePath={(docId) => `/backoffice/nominee_shareholder_types/${type.id}/document_templates/${docId}`}
        />
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {type.name}?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete this nominee shareholder type. Types with existing services cannot be deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => router.delete(`/backoffice/nominee_shareholder_types/${type.id}`)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </BackofficeLayout>
  )
}
